/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Message, ICON_CHECK,
  HStack, ActionIcon, Badge,
  ICON_CLOSE, Select,
  AutoCompleteV2,
  AutoCompleteV2OnSuggestionSelected,
  InputAction,
  CheckboxToggle,
  ICON_ALERT_CIRCLE_OUTLINE,
  Action,
  Grid,
  Image,
  Text,
  ICON_ARROW_UP,
} from '@doveit/bricks';
import { formatDate } from '@doveit/hammer';
import Gallery, { GalleryItem } from '../../../components/gallery/Gallery';
import { Content } from '../../../providers/api/dtos';
import useAllShootingsByPropertyId from '../../hooks/use-all-shootings-by-property-id/useAllShootingsByPropertyId';
import { ContentAbout, ContentAboutType, ShootingStatus } from '../../../domain/types';
import { contentAboutLabels } from '../../../labels';
import { SortOrder } from '../../../providers/pagination';
import MultimediaContentThumbnail from '../../../content/components/MultimediaContentThumbnail';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import * as styles from './ShootingPhotoSelection.style';

export type ShootingPhotoItem = GalleryItem & { fileName: string, shootingId?: string, content: Content, hasUnsavedChanges?: boolean };

export interface ContentAboutSuggestion {
  value: ContentAboutType,
  label: string,
}

export const printSuggestion = (suggestion: ContentAboutSuggestion): string => suggestion.label;

const allSuggestions: ContentAboutSuggestion[] = Object.values(ContentAbout).map((contentAbout) => ({
  value: contentAbout as ContentAboutType,
  label: contentAboutLabels[contentAbout as ContentAboutType],
}));

const ContentAboutActionAutocomplete: React.FC<{
  content: Content;
  onUpdateAbout: (fileName: string, about?: string) => void;
  disabled?: boolean;
  isMobile?: boolean;
}> = ({
  content, onUpdateAbout, disabled, isMobile = false,
}) => {
  const [descriptionInput, setDescriptionInput] = React.useState<string | undefined>(
    content.about ? contentAboutLabels[content.about as ContentAboutType] : undefined,
  );
  const [selectedSuggestion, setSelectedSuggestion] = React.useState<ContentAboutSuggestion | undefined>(
    content.about ? {
      value: content.about as ContentAboutType,
      label: contentAboutLabels[content.about as ContentAboutType],
    } : undefined,
  );

  React.useEffect(() => {
    setDescriptionInput(content.about ? contentAboutLabels[content.about as ContentAboutType] : undefined);
    setSelectedSuggestion(content.about ? {
      value: content.about as ContentAboutType,
      label: contentAboutLabels[content.about as ContentAboutType],
    } : undefined);
  }, [content.fileName, content.about]);

  const suggestions: ContentAboutSuggestion[] = React.useMemo(() => {
    if (!descriptionInput) {
      return allSuggestions;
    }
    return allSuggestions.filter((suggestion) => suggestion.label.toLocaleLowerCase().includes(descriptionInput.toLocaleLowerCase()));
  }, [descriptionInput]);

  const onContentAboutSelected: AutoCompleteV2OnSuggestionSelected<ContentAboutSuggestion | null> = React.useCallback(async (suggestion) => {
    setDescriptionInput(suggestion ? suggestion.label : undefined);
    setSelectedSuggestion(suggestion ? {
      value: suggestion.value,
      label: suggestion.label,
    } : undefined);
    onUpdateAbout(content.fileName, suggestion?.value);
  }, [content.fileName, onUpdateAbout]);

  const onDescriptionInputChange = React.useCallback((searchText: string, action: InputAction) => {
    switch (action) {
      case 'input-change':
        setDescriptionInput(searchText);
        break;
      case 'input-blur':
        setDescriptionInput(selectedSuggestion?.label || '');
        break;
      default:
    }
  }, [selectedSuggestion]);

  return (
    <div css={styles.aboutAutocomplete} $isMobile={isMobile}>
      <AutoCompleteV2
        aria-label="Autocomplete per modificare la descrizione del contenuto"
        suggestions={suggestions}
        printSuggestion={printSuggestion}
        onSuggestionSelected={onContentAboutSelected}
        minLength={1}
        value={selectedSuggestion}
        placeholder="Descrizione"
        clearValueOnSelection
        onInputChange={onDescriptionInputChange}
        inputValue={descriptionInput || ''}
        disabled={disabled}
      />
    </div>
  );
};

export interface ShootingPhotoSelectionProps {
  propertyId: number;
  photos: Content[];
  selectedNames: string[];
  modifiedAbouts?: Record<string, string | undefined>;
  isSaving: boolean;
  onToggleSelection: (fileName: string) => void;
  onSelectMultiple?: (fileNames: string[]) => void;
  onDeselectMultiple?: (fileNames: string[]) => void;
  onUpdateAbout?: (fileName: string, about?: string) => void;
}

const ShootingPhotoSelection: React.FC<ShootingPhotoSelectionProps> = ({
  propertyId,
  photos,
  selectedNames,
  modifiedAbouts = {},
  isSaving,
  onToggleSelection,
  onSelectMultiple,
  onDeselectMultiple,
  onUpdateAbout,
}) => {
  const { data: shootings } = useAllShootingsByPropertyId(propertyId, { status: [ShootingStatus.COMPLETED, ShootingStatus.CLOSED] }, { page: 0, size: 20, sort: { dueDate: SortOrder.DESC } });
  const [selectedShootingId, setSelectedShootingId] = React.useState<string>('all');
  const [showModifications, setShowModifications] = React.useState(false);
  const isMobile = useIsDevice('mobile');
  // On mobile the preview of a single photo replaces the thumbnails list
  const [detailFileName, setDetailFileName] = React.useState<string | undefined>();

  const galleryItems = React.useMemo(() => photos.map((file, index) => {
    const currentAbout = file.fileName in modifiedAbouts ? modifiedAbouts[file.fileName] : file.about;
    const isSelected = selectedNames.includes(file.fileName);
    const hasWarning = isSelected !== file.selected || currentAbout !== file.about;

    return {
      id: index + 1,
      fileName: file.fileName,
      picture: file.url,
      thumbnail: file.thumbnail,
      selected: isSelected,
      shootingId: file.shootingId,
      content: { ...file, about: currentAbout },
      hasWarning: showModifications && hasWarning,
      hasUnsavedChanges: hasWarning,
    };
  }) as ShootingPhotoItem[], [photos, selectedNames, modifiedAbouts, showModifications]);

  const shouldRenderShootingFilter = React.useMemo(
    () => (Array.isArray(shootings) && shootings.filter((shooting) => shooting.files.length > 0).length > 1),
    [shootings],
  );

  const filteredGalleryItems = React.useMemo(
    () => galleryItems.map((item) => ({ ...item, shown: selectedShootingId === 'all' || item.shootingId === selectedShootingId })),
    [galleryItems, selectedShootingId],
  );

  const shownGalleryItems = React.useMemo(
    () => filteredGalleryItems.filter((item) => item.shown),
    [filteredGalleryItems],
  );

  const shootingOptions = React.useMemo(() => {
    const options = [{ label: 'Tutti gli shooting', value: 'all' }];

    shootings?.forEach((shooting) => {
      if (shooting.id) {
        options.push({
          label: `${shooting.photographerName} - ${formatDate(new Date(shooting.dueDate))}`,
          value: shooting.id,
        });
      }
    });

    return options;
  }, [shootings]);

  const handleSelectAllFiltered = React.useCallback(() => {
    if (onSelectMultiple) {
      onSelectMultiple(filteredGalleryItems.filter((item) => item.shown).map((item) => item.fileName));
    }
  }, [filteredGalleryItems, onSelectMultiple]);

  const handleDeselectAllFiltered = React.useCallback(() => {
    if (onDeselectMultiple) {
      onDeselectMultiple(filteredGalleryItems.filter((item) => item.shown).map((item) => item.fileName));
    }
  }, [filteredGalleryItems, onDeselectMultiple]);

  const renderActionsSlot = React.useCallback(() => (
    <HStack gap={100}>
      <Action
        label="Seleziona tutte"
        aria-label="Seleziona tutte le foto dello shooting visualizzato"
        emphasis="low"
        size="S"
        onClick={handleSelectAllFiltered}
        disabled={isSaving}
      />
      <Action
        label="Deseleziona tutte"
        aria-label="Deseleziona tutte le foto dello shooting visualizzato"
        emphasis="low"
        size="S"
        onClick={handleDeselectAllFiltered}
        disabled={isSaving}
      />
      <CheckboxToggle
        aria-label="Mostra modifiche su foto"
        text={showModifications ? 'Nascondi modifiche su anteprima' : 'Mostra modifiche su anteprima'}
        size="S"
        checked={showModifications}
        onChange={(e) => setShowModifications(e.target.checked)}
      />
    </HStack>
  ), [handleDeselectAllFiltered, handleSelectAllFiltered, isSaving, showModifications]);

  const renderItemActionsSlot = React.useCallback(({ activeItem }: any) => {
    if (!activeItem) return null;

    const isSelected = selectedNames.includes(activeItem.fileName);

    return (
      <HStack
        gap={150}
        alignItems="center"
      >
        {onUpdateAbout && (
          <ContentAboutActionAutocomplete
            key={activeItem.fileName}
            content={activeItem.content}
            onUpdateAbout={onUpdateAbout}
            disabled={isSaving}
          />
        )}
        <ActionIcon
          color={isSelected ? 'primary' : 'neutral'}
          icon={{ path: isSelected ? ICON_CLOSE : ICON_CHECK }}
          label={isSelected ? 'Deseleziona foto' : 'Seleziona foto'}
          onClick={() => onToggleSelection(activeItem.fileName)}
          disabled={isSaving}
        />
      </HStack>
    );
  }, [selectedNames, isSaving, onToggleSelection, onUpdateAbout]);

  const renderTagsSlot = React.useCallback(({ activeItem }: any) => {
    if (!activeItem) return null;

    const isSelected = selectedNames.includes(activeItem.fileName);

    return (
      <HStack gap={100} alignItems="center">
        {isSelected && (
          <Badge size="XS" label="Selezionata" color="primary" icon={ICON_CHECK} />
        )}
        {activeItem.hasUnsavedChanges && showModifications && (
          <Badge size="XS" label="Modifiche non salvate" color="warning" icon={ICON_ALERT_CIRCLE_OUTLINE} />
        )}
      </HStack>
    );
  }, [selectedNames, showModifications]);

  const renderToolsSlot = React.useCallback(() => (
    <Select
      aria-label="Filtra per shooting"
      value={selectedShootingId}
      options={shootingOptions}
      size="S"
      onChange={(e) => setSelectedShootingId(e.target.value as string)}
      disabled={isSaving}
      width="100%"
    />
  ), [shootingOptions, selectedShootingId, isSaving]);

  if (!galleryItems || galleryItems.length === 0) {
    return (
      <div className="shooting-photo-selection" css={styles.empty} $isMobile={isMobile}>
        {shouldRenderShootingFilter && renderToolsSlot()}
        <Message type="info" message="Nessuna foto presente per questo filtro." />
      </div>
    );
  }

  if (isMobile) {
    const detailIndex = shownGalleryItems.findIndex((item) => item.fileName === detailFileName);
    const detailItem = detailIndex >= 0 ? shownGalleryItems[detailIndex] : undefined;

    if (detailItem) {
      return (
        <div className="shooting-photo-selection" css={styles.mobileDetail} aria-label="Dettaglio foto (mobile)">
          <div css={styles.mobileDetailHeader}>
            <Action
              label="Torna all'elenco"
              aria-label="Torna all'elenco delle foto"
              iconLeft={{ path: ICON_ARROW_UP, direction: 'left' }}
              emphasis="low"
              size="S"
              onClick={() => setDetailFileName(undefined)}
            />
            <HStack gap={100} alignItems="center" wrap="nowrap">
              <ActionIcon
                size="S"
                icon={{ path: ICON_ARROW_UP, direction: 'left' }}
                label="Vai alla foto precedente"
                aria-label="Vai alla foto precedente"
                onClick={() => setDetailFileName(shownGalleryItems[detailIndex - 1].fileName)}
                disabled={detailIndex === 0}
              />
              <Text.BodySmall as="span">{detailIndex + 1} / {shownGalleryItems.length}</Text.BodySmall>
              <ActionIcon
                size="S"
                icon={{ path: ICON_ARROW_UP, direction: 'right' }}
                label="Vai alla foto successiva"
                aria-label="Vai alla foto successiva"
                onClick={() => setDetailFileName(shownGalleryItems[detailIndex + 1].fileName)}
                disabled={detailIndex === shownGalleryItems.length - 1}
              />
            </HStack>
          </div>
          <div css={styles.mobileDetailImage} $selected={detailItem.selected}>
            <Image
              src={detailItem.picture ?? detailItem.thumbnail}
              alt={`Foto ${detailItem.id}`}
              responsive
            />
            <div css={styles.mobileDetailBottomBar}>
              <HStack gap={100} alignItems="center">
                {detailItem.selected && (
                  <Badge size="XS" label="Selezionata" color="primary" icon={ICON_CHECK} />
                )}
                {detailItem.hasUnsavedChanges && (
                  <Badge size="XS" label="Modifiche non salvate" color="warning" icon={ICON_ALERT_CIRCLE_OUTLINE} />
                )}
              </HStack>
              <div css={styles.mobileDetailFileName} title={detailItem.fileName}>
                <Badge aria-label="Badge nome file" size="XS" label={detailItem.fileName} />
              </div>
            </div>
          </div>
          {onUpdateAbout && (
            <ContentAboutActionAutocomplete
              key={detailItem.fileName}
              content={detailItem.content}
              onUpdateAbout={onUpdateAbout}
              disabled={isSaving}
              isMobile
            />
          )}
          <Action
            // Same colors of the selection action icon of the desktop gallery
            color={detailItem.selected ? 'primary' : 'neutral'}
            iconLeft={{ path: detailItem.selected ? ICON_CLOSE : ICON_CHECK }}
            label={detailItem.selected ? 'Deseleziona foto' : 'Seleziona foto'}
            aria-label={detailItem.selected ? 'Deseleziona foto' : 'Seleziona foto'}
            onClick={() => onToggleSelection(detailItem.fileName)}
            disabled={isSaving}
            expanded
          />
        </div>
      );
    }

    return (
      <div className="shooting-photo-selection" css={styles.mobileBase} aria-label="Selezione foto (mobile)">
        <div css={styles.mobileToolbar}>
          {shouldRenderShootingFilter && renderToolsSlot()}
          <div css={styles.mobileBulkActions}>
            <Action
              label="Seleziona tutte"
              aria-label="Seleziona tutte le foto dello shooting visualizzato"
              emphasis="low"
              size="S"
              onClick={handleSelectAllFiltered}
              disabled={isSaving}
              expanded
            />
            <Action
              label="Deseleziona tutte"
              aria-label="Deseleziona tutte le foto dello shooting visualizzato"
              emphasis="low"
              size="S"
              onClick={handleDeselectAllFiltered}
              disabled={isSaving}
              expanded
            />
          </div>
          <Text.BodySmall>Tocca una foto per vederla e selezionarla.</Text.BodySmall>
        </div>
        <div css={styles.mobileThumbnails} aria-label="Elenco foto dello shooting">
          {shownGalleryItems.length === 0 ? (
            <Message type="info" message="Nessuna foto presente per questo filtro." />
          ) : (
            <Grid gutter={25}>
              {shownGalleryItems.map((item) => (
                <Grid.Unit size={1 / 3} key={item.id}>
                  <MultimediaContentThumbnail
                    aria-label={`Foto ${item.id}`}
                    picture={{ src: item.thumbnail ?? item.picture, alt: `Foto ${item.id}` }}
                    index={item.id as number}
                    aspectRatio="3 / 2"
                    selected={item.selected}
                    // On mobile there is no room for a toggle: unsaved changes are always highlighted
                    hasWarning={item.hasUnsavedChanges}
                    onClick={() => setDetailFileName(item.fileName)}
                  />
                </Grid.Unit>
              ))}
            </Grid>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="shooting-photo-selection">
      <Gallery<ShootingPhotoItem>
        items={filteredGalleryItems}
        toolsSlot={shouldRenderShootingFilter ? renderToolsSlot : undefined}
        actionsSlot={renderActionsSlot}
        itemActionsSlot={renderItemActionsSlot}
        tagsSlot={renderTagsSlot}
      />
    </div>
  );
};

export default ShootingPhotoSelection;
